import type React from "react";
import { useParams } from "react-router";
import { twJoin } from "tailwind-merge";

import { Overlay } from "@/components/Overlay/Overlay";
import { ProjectList } from "@/components/ProjectSwitcher/ProjectList";
import { useRecentProjects } from "@/components/ProjectSwitcher/useRecentProjects";
import { useSidebarManager } from "@/hooks/useSidebar";
import { useProjectContext } from "@/providers/ProjectContext";
import { useTranslation } from "@/translations";
import { type RuntimeFloatingPanelAttributes, RuntimeFloatingPortal } from "@/ui-adapters/RuntimeFloatingPanel";

interface PortfolioSidebarProjectSwitcherProps {
  floating: RuntimeFloatingPanelAttributes;
  isOpened: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchQueryChange: (searchQuery: string) => void;
}

export function PortfolioSidebarProjectSwitcher({
  floating,
  isOpened,
  onClose,
  searchQuery,
  onSearchQueryChange,
}: PortfolioSidebarProjectSwitcherProps): React.ReactNode {
  const { projectId } = useProjectContext();
  const params = useParams();
  const { isCollapsed } = useSidebarManager();
  const { t } = useTranslation();

  const isInProject = !!params["slug"];
  const { projects } = useRecentProjects(projectId, !isInProject, 3);

  if (!isOpened) return null;

  return (
    <RuntimeFloatingPortal>
      <div
        id="portfolio-sidebar-project-switcher"
        aria-label={t("component.project-switcher.search.placeholder")}
        className={twJoin(
          "z-50 w-screen max-w-overlay-fluid md:w-compact-preview",
          isCollapsed ? "-top-4! left-4!" : "-top-14!",
        )}
        role="dialog"
        {...floating}
      >
        <div className="overflow-hidden rounded-lg bg-background-default shadow-raised">
          <ProjectList
            currentProjectId={projectId}
            projects={projects}
            maxProjects={3}
            showCurrentProject={!isInProject}
            onClose={onClose}
            autoFocusSearchInput
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
          />
        </div>
      </div>
      <Overlay onClick={onClose} />
    </RuntimeFloatingPortal>
  );
}
